import {
  usePlatformOrganizationsList as usePlatformOrganizationsListQuery,
  usePlatformOrganizationsRead as usePlatformOrganizationsReadQuery,
  useOrganizationBranchesList as useOrganizationBranchesListQuery,
  useOrganizationBranchesRead as useOrganizationBranchesReadQuery,
  useOrganizationClientsList as useOrganizationClientsListQuery,
  useOrganizationClientsRead as useOrganizationClientsReadQuery,
  useOrganizationClientAccountsList as useOrganizationClientAccountsListQuery,
  useOrganizationClientAccountsRead as useOrganizationClientAccountsReadQuery,
  useOrganizationPortfoliosList as useOrganizationPortfoliosListQuery,
  useOrganizationPortfoliosRead as useOrganizationPortfoliosReadQuery,
  useOrganizationAuditList as useOrganizationAuditListQuery,
  type PlatformOrganizationViewDto,
  type BranchSurfaceDto,
  type ClientAccountSurfaceDto,
  type PortfolioAccountSurfaceDto,
  type AuditLogSurfaceDto,
} from '../../api/generated/client';
import { ROUTE_IDS, OPERATION_IDS } from '@finance/contracts';
import type { ListHookResult, GetHookResult, ResourceHookBinding } from '../resource-hook-registry.js';
import { selectApiData, type ApiSuccessEnvelope } from '../../api/select-api-data.js';

function selectListRows<T>(response: unknown): T[] {
  return selectApiData(response as ApiSuccessEnvelope<T[]>) ?? [];
}

function selectDetail<T>(response: unknown): T {
  return selectApiData(response as ApiSuccessEnvelope<T>);
}

function selectAuditLogRows(response: unknown): AuditLogSurfaceDto[] {
  const dto = selectApiData(
    response as ApiSuccessEnvelope<{
      data: AuditLogSurfaceDto[];
      page: number;
      totalPages: number;
      total: number;
    }>,
  );
  return dto.data;
}

export function usePlatformOrganizationList(): ListHookResult<PlatformOrganizationViewDto[]> {
  return usePlatformOrganizationsListQuery({ query: { select: selectListRows<PlatformOrganizationViewDto> } });
}

export function usePlatformOrganizationGet(id: string): GetHookResult<PlatformOrganizationViewDto> {
  return usePlatformOrganizationsReadQuery(id, { query: { select: selectDetail<PlatformOrganizationViewDto> } });
}

export function useOrganizationBranchList(): ListHookResult<BranchSurfaceDto[]> {
  return useOrganizationBranchesListQuery({ query: { select: selectListRows<BranchSurfaceDto> } });
}

export function useOrganizationBranchGet(id: string): GetHookResult<BranchSurfaceDto> {
  return useOrganizationBranchesReadQuery(id, { query: { select: selectDetail<BranchSurfaceDto> } });
}

export function useOrganizationClientList(): ListHookResult<ClientAccountSurfaceDto[]> {
  return useOrganizationClientsListQuery({ query: { select: selectListRows<ClientAccountSurfaceDto> } });
}

export function useOrganizationClientGet(id: string): GetHookResult<ClientAccountSurfaceDto> {
  return useOrganizationClientsReadQuery(id, { query: { select: selectDetail<ClientAccountSurfaceDto> } });
}

export function useOrganizationClientAccountList(): ListHookResult<ClientAccountSurfaceDto[]> {
  return useOrganizationClientAccountsListQuery({ query: { select: selectListRows<ClientAccountSurfaceDto> } });
}

export function useOrganizationClientAccountGet(id: string): GetHookResult<ClientAccountSurfaceDto> {
  return useOrganizationClientAccountsReadQuery(id, { query: { select: selectDetail<ClientAccountSurfaceDto> } });
}

export function useOrganizationPortfolioList(): ListHookResult<PortfolioAccountSurfaceDto[]> {
  return useOrganizationPortfoliosListQuery({ query: { select: selectListRows<PortfolioAccountSurfaceDto> } });
}

export function useOrganizationPortfolioGet(id: string): GetHookResult<PortfolioAccountSurfaceDto> {
  return useOrganizationPortfoliosReadQuery(id, { query: { select: selectDetail<PortfolioAccountSurfaceDto> } });
}

export function useOrganizationAuditLogList(): ListHookResult<AuditLogSurfaceDto[]> {
  return useOrganizationAuditListQuery(undefined, { query: { select: selectAuditLogRows } });
}

export const PLATFORM_ORGANIZATIONS_HOOKS: ResourceHookBinding<PlatformOrganizationViewDto[], PlatformOrganizationViewDto> = {
  useList: usePlatformOrganizationList,
  selectList: selectListRows<PlatformOrganizationViewDto>,
  useGet: usePlatformOrganizationGet,
  selectDetail: selectDetail<PlatformOrganizationViewDto>,
  rowIdExtractor: (row) => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'organizationId',
  paramName: 'organizationId',
  listRouteId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
  detailRouteId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_DETAIL,
  readOperationId: OPERATION_IDS.PLATFORM_ORGANIZATIONS_READ,
};

export const ORGANIZATION_BRANCHES_HOOKS: ResourceHookBinding<BranchSurfaceDto[], BranchSurfaceDto> = {
  useList: useOrganizationBranchList,
  selectList: selectListRows<BranchSurfaceDto>,
  useGet: useOrganizationBranchGet,
  selectDetail: selectDetail<BranchSurfaceDto>,
  rowIdExtractor: (row) => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'branchId',
  paramName: 'branchId',
  listRouteId: ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
  detailRouteId: ROUTE_IDS.ORGANIZATION_BRANCHES_DETAIL,
  readOperationId: OPERATION_IDS.ORGANIZATION_BRANCHES_READ,
};

export const ORGANIZATION_CLIENTS_HOOKS: ResourceHookBinding<ClientAccountSurfaceDto[], ClientAccountSurfaceDto> = {
  useList: useOrganizationClientList,
  selectList: selectListRows<ClientAccountSurfaceDto>,
  useGet: useOrganizationClientGet,
  selectDetail: selectDetail<ClientAccountSurfaceDto>,
  rowIdExtractor: (row) => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'clientId',
  paramName: 'clientId',
  listRouteId: ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
  detailRouteId: ROUTE_IDS.ORGANIZATION_CLIENTS_DETAIL,
  readOperationId: OPERATION_IDS.ORGANIZATION_CLIENTS_READ,
};

export const ORGANIZATION_CLIENT_ACCOUNTS_HOOKS: ResourceHookBinding<ClientAccountSurfaceDto[], ClientAccountSurfaceDto> = {
  useList: useOrganizationClientAccountList,
  selectList: selectListRows<ClientAccountSurfaceDto>,
  useGet: useOrganizationClientAccountGet,
  selectDetail: selectDetail<ClientAccountSurfaceDto>,
  rowIdExtractor: (row) => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'accountId',
  paramName: 'accountId',
  listRouteId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
  detailRouteId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_DETAIL,
  readOperationId: OPERATION_IDS.ORGANIZATION_CLIENT_ACCOUNTS_READ,
};

export const ORGANIZATION_PORTFOLIOS_HOOKS: ResourceHookBinding<PortfolioAccountSurfaceDto[], PortfolioAccountSurfaceDto> = {
  useList: useOrganizationPortfolioList,
  selectList: selectListRows<PortfolioAccountSurfaceDto>,
  useGet: useOrganizationPortfolioGet,
  selectDetail: selectDetail<PortfolioAccountSurfaceDto>,
  rowIdExtractor: (row) => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'portfolioId',
  paramName: 'portfolioId',
  listRouteId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
  detailRouteId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_DETAIL,
  readOperationId: OPERATION_IDS.ORGANIZATION_PORTFOLIOS_READ,
};

export const ORGANIZATION_AUDIT_HOOKS: ResourceHookBinding<AuditLogSurfaceDto[], AuditLogSurfaceDto> = {
  useList: useOrganizationAuditLogList,
  selectList: selectAuditLogRows,
  rowIdExtractor: (row) => (typeof row.id === 'string' ? row.id : undefined),
  rowIdField: 'id',
  routeParameterName: 'auditId',
  paramName: 'auditId',
  listRouteId: ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
  detailRouteId: ROUTE_IDS.ORGANIZATION_AUDIT_DETAIL,
  readOperationId: OPERATION_IDS.ORGANIZATION_AUDIT_READ,
};

export const ORGANIZATION_MODULE_ACTIVATIONS_HOOKS: ResourceHookBinding = {
  rowIdExtractor: (row) => (typeof row.moduleId === 'string' ? row.moduleId : undefined),
  rowIdField: 'moduleId',
  routeParameterName: 'moduleId',
  paramName: 'moduleId',
  listRouteId: ROUTE_IDS.ORGANIZATION_MODULES_LIST,
  detailRouteId: ROUTE_IDS.ORGANIZATION_MODULES_CONFIGURE,
  readOperationId: OPERATION_IDS.ORGANIZATION_MODULES_LIST_ASSIGNED,
};
